import React, { useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { isZebraStickerPrinter, stickerElementToBase64 } from '../utils/thermalStickerPdf';

const StickerContainer = styled.div`
  width: 4in;
  height: 6in;
  background: white;
  font-family: 'Montserrat', sans-serif;
  color: #222;
  position: relative;
  overflow: hidden;
  padding: 0.25in;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5in;
`;

const HeaderItem = styled.div`
  display: flex;
  gap: 0.25in;
`;

const Label = styled.div`
  font-size: 12pt;
  text-transform: uppercase;
  color: #222222;
  font-weight: normal;
`;

const Value = styled.div`
  font-size: 18pt;
  font-weight: bold;
  color: #222222;
  margin-left: 0.1in;
  text-transform: uppercase;
`;

const DiagonalLayout = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  background: white;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, white 49.9%, #222222 50%, #222222 50.1%, white 50.2%);
    z-index: 1;
  }
`;

const TopSection = styled.div`
  position: absolute;
  top: 0.25in;
  left: 0.25in;
  width: 1.9in;
  z-index: 3;
  background: transparent;
  padding: 0.1in 0.12in 0.15in 0.1in;
`;

const CornerBlock = styled.div`
  position: absolute;
  top: 0.25in;
  right: 0.25in;
  width: 1.7in;
  z-index: 4;
  background: white;
  text-align: right;
  padding: 0.1in 0.08in 0.16in 0.1in;
`;

const BottomSection = styled.div`
  position: absolute;
  left: 0.25in;
  right: 0.25in;
  bottom: 0.25in;
  text-align: right;
  z-index: 2;
  background: transparent;
  padding: 0.1in 0;
`;

const BoxCount = styled.div`
  position: absolute;
  bottom: 0.25in;  // Move to bottom
  left: 0.25in;
  font-size: 24pt;
  font-weight: bold;
  color: #222222;
  z-index: 2;
  background: white;
  padding: 0.1in;
  // Add margin-top to ensure it's below the diagonal
  margin-top: 0.5in;
`;

const CompanyInfo = styled.div`
  margin-top: 1in;
  text-align: right;
  font-size: 10pt;
  line-height: 1.5;
  background: white;
  padding: 0.1in;
  color: #222222;
`;

const BoxSticker = ({ 
  invoiceNumber, 
  client, 
  total, 
  manager, 
  pieces, 
  currentBox, 
  totalBoxes,
  onPdfGenerated 
}) => {
  const stickerRef = useRef(null);
  const generatingRef = useRef(false);
  const strawberryPatch = isZebraStickerPrinter();

  useEffect(() => {
    const generatePdfIfReady = async () => {
      if (!stickerRef.current || !onPdfGenerated || generatingRef.current) {
        return;
      }

      try {
        generatingRef.current = true;
        const element = stickerRef.current;
        const zebra = isZebraStickerPrinter();
        const opt = {
          margin: 0,
          filename: `box_sticker_${invoiceNumber}_${currentBox}.pdf`,
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { 
            scale: 4,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: true,
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.querySelector('[data-sticker]');
              if (clonedElement) {
                clonedElement.style.width = '4in';
                clonedElement.style.height = '6in';
                clonedElement.style.backgroundColor = 'white';
                clonedElement.style.fontFamily = "'Montserrat', sans-serif";
                clonedElement.style.textTransform = 'uppercase';
                clonedElement.style.transform = 'scale(1)';
                clonedElement.style.transformOrigin = 'top left';
              }
            }
          },
          jsPDF: { 
            unit: 'in',
            format: [4, 6],
            orientation: 'portrait',
            compress: true,
            precision: 16
          }
        };

        const base64data = await stickerElementToBase64(element, opt, { zebra });
        onPdfGenerated(base64data);
        generatingRef.current = false;
      } catch (error) {
        console.error('Error generating PDF:', error);
        generatingRef.current = false;
        throw error;
      }
    };

    generatePdfIfReady();
  }, [invoiceNumber, currentBox, onPdfGenerated]);

  return (
    <div 
      ref={stickerRef}
      data-sticker
      style={{
        width: '4in',
        height: '6in',
        backgroundColor: 'white',
        fontFamily: "'Montserrat', sans-serif",
        textTransform: 'uppercase',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <DiagonalLayout>
        <TopSection>
          <div style={{ marginBottom: '0.5in', background: 'white' }}>
            <Label>Order No.</Label>
            <Value>{String(invoiceNumber || '').toUpperCase()}</Value>
          </div>
          
          <div style={{ marginBottom: '0.35in', background: 'white' }}>
            <Label>Total</Label>
            <Value>${total}</Value>
          </div>
        </TopSection>

        <CornerBlock>
          <div style={{ marginBottom: '0.28in' }}>
            <Label>Manager</Label>
            <Value style={{ lineHeight: 1.35 }}>{String(manager || '').toUpperCase()}</Value>
          </div>
          <div>
            <Label>Pieces</Label>
            <Value style={{ lineHeight: 1.35 }}>{pieces}</Value>
          </div>
        </CornerBlock>
        
        <BottomSection>
          <div style={{ marginBottom: '0.35in', background: 'white', width: '100%', paddingBottom: '0.06in' }}>
            <Label>Client</Label>
            <Value style={{ lineHeight: 1.2, whiteSpace: 'normal' }}>{String(client || '').toUpperCase()}</Value>
          </div>
          
          <CompanyInfo>
            {strawberryPatch ? (
              <>
                <div>STRAWBERRY PATCH</div>
                <div>110 SW STATE ST</div>
                <div>ANKENY, IA 50023</div>
                <div>(515) 965-1372</div>
              </>
            ) : (
              <>
                <div>EIGHT SEVEN CENTRAL</div>
                <div>863 42ND STREET</div>
                <div>DES MOINES, IOWA</div>
                <div>EIGHTSEVENCENTRAL.COM</div>
              </>
            )}
          </CompanyInfo>
        </BottomSection>

        {/* Box count moved outside TopSection */}
        <BoxCount>{currentBox} of {totalBoxes}</BoxCount>
      </DiagonalLayout>
    </div>
  );
};

export default BoxSticker; 